import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { CreditCard, TrendingUp, CheckCircle2, Clock, Loader2 } from "lucide-react";

const PROVIDER_LABELS = {
  manarecharge: "ManaRecharge",
  razorpay: "Razorpay",
  cashfree: "Cashfree",
  runpaisa: "Runpaisa",
};

const PROVIDER_COLORS = {
  manarecharge: "bg-blue-50 text-blue-700 border-blue-200",
  razorpay: "bg-indigo-50 text-indigo-700 border-indigo-200",
  cashfree: "bg-green-50 text-green-700 border-green-200",
  runpaisa: "bg-orange-50 text-orange-700 border-orange-200",
};

export default function PaymentSummary() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/payment-links/summary/dashboard")
      .then(({ data }) => setData(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;
  if (!data) return null;

  const sc = data.status_counts || {};
  const ps = data.provider_stats || {};

  return (
    <div className="space-y-4" data-testid="payment-summary">
      {/* Top-level stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={CreditCard} label="Total links" value={data.total_links} />
        <StatCard icon={CheckCircle2} label="Paid" value={sc.paid || 0} color="text-emerald-600" />
        <StatCard icon={Clock} label="Pending" value={sc.pending || 0} color="text-amber-600" />
        <StatCard icon={TrendingUp} label="Total paid" value={`₹${Number(data.paid_amount || 0).toLocaleString("en-IN")}`} color="text-emerald-600" />
      </div>

      {/* Provider-wise breakdown */}
      {Object.keys(ps).length > 0 && (
        <div className="surface-card p-4">
          <h4 className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-3">Provider-wise Breakdown</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left px-3 py-2 font-medium">Provider</th>
                  <th className="text-right px-3 py-2 font-medium">Total links</th>
                  <th className="text-right px-3 py-2 font-medium">Paid</th>
                  <th className="text-right px-3 py-2 font-medium">Total amount</th>
                  <th className="text-right px-3 py-2 font-medium">Paid amount</th>
                  <th className="text-right px-3 py-2 font-medium">Success rate</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(ps).map(([provider, stats]) => (
                  <tr key={provider} className="border-b last:border-0">
                    <td className="px-3 py-2">
                      <Badge variant="outline" className={`text-[10px] ${PROVIDER_COLORS[provider] || ""}`}>
                        {PROVIDER_LABELS[provider] || provider}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{stats.total}</td>
                    <td className="px-3 py-2 text-right font-mono text-emerald-600">{stats.paid}</td>
                    <td className="px-3 py-2 text-right font-mono">₹{Number(stats.amount || 0).toLocaleString("en-IN")}</td>
                    <td className="px-3 py-2 text-right font-mono text-emerald-600">₹{Number(stats.paid_amount || 0).toLocaleString("en-IN")}</td>
                    <td className="px-3 py-2 text-right font-mono">{stats.total > 0 ? `${((stats.paid / stats.total) * 100).toFixed(1)}%` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color = "" }) {
  return (
    <div className="surface-card p-3">
      <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground uppercase tracking-wider"><Icon className="h-3 w-3" /> {label}</div>
      <div className={`font-mono font-bold text-xl mt-1 ${color}`}>{value}</div>
    </div>
  );
}
